// @flow
import { call, cancel, fork, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { CHANGE_SELECTED_DAY, LOAD_PAGE, RECEIVE_EVENTS, UNLOAD_PAGE } from './constants';
import { changeSelectedDay, pageReady, receiveEvents } from './actions';
import { delay } from 'redux-saga';
import type { GetContactSensorEventsAction, LoadContactSensorPageAction, LoadPageAction } from './types';
import { getCurrentTime, getTimezone } from '~/core/state/hub/selectors';

import { alerts, routes } from '~/core/constants';
import { deviceTypes } from '~/core/constants/devices';
import { GET_PRODUCTS_INTERVAL } from '~/core/state/hub/constants';
import { getEvents } from '~/core/api/sensor-events';
import { getSelectedDay } from './selectors';
import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';
import type { Effect } from 'redux-saga';
import messages from './intl';
import moment from 'moment-timezone';
import { navigate } from '~/core/state/navigate';
import { noop } from 'lodash/fp';
import { requestAlert } from '~/core/state/alerts/actions';

export function* getContactSensorEvents({
    payload: { id, day }
}: GetContactSensorEventsAction): Generator<Effect | noop, *, *> {
    const timezone = yield select(getTimezone);
    const startOfDay = moment.tz(day, timezone).startOf('day');
    const from = startOfDay.valueOf();
    const to = moment
        .tz(day, timezone)
        .endOf('day')
        .valueOf();

    const [response, error] = yield call(handleApi(getEvents), deviceTypes.CONTACT_SENSOR, id, from, to);

    if (error) {
        yield put(requestAlert(messages.getEventsError, alerts.types.ERROR));
    } else {
        yield put(receiveEvents(startOfDay.format('YYYY-MM-DD'), response, id));
    }
}

export function* watchChangeSelectedDay(): Generator<Effect, *, *> {
    yield takeEvery(CHANGE_SELECTED_DAY, handleSaga(getContactSensorEvents));
}

export function* loadPage({ payload: { id, now, timezone } }: LoadPageAction): Generator<Effect, *, *> {
    const startOfDay = moment.tz(now, timezone).startOf('day');
    yield put(changeSelectedDay({ id, day: startOfDay.format('YYYY-MM-DD') }));

    const { receiveEvents } = yield race({
        receiveEvents: take(RECEIVE_EVENTS),
        unloadPage: take(UNLOAD_PAGE)
    });

    if (receiveEvents) {
        if (receiveEvents.error) {
            yield put(navigate(routes.devices));
            return;
        }

        yield put(pageReady({ id }));
    }
}

function* loadContactSensorPageSaga({ payload: { id } }: LoadContactSensorPageAction): Generator<Effect, *, *> {
    while (true) {
        let selectedDay = yield select(getSelectedDay, { id });
        const timezone = yield select(getTimezone);

        if (!selectedDay) {
            selectedDay = yield select(getCurrentTime);
        }

        const selectedDate = moment.tz(selectedDay, timezone);

        yield call(loadPage, {
            payload: { id, now: selectedDate.format(), timezone: selectedDate.tz() }
        });
        yield call(delay, GET_PRODUCTS_INTERVAL);
    }
}

function* watchLoadContactSensorPageSaga(): Generator<Effect, *, *> {
    while (true) {
        const action = yield take(LOAD_PAGE);
        const deamon = yield fork(loadContactSensorPageSaga, action);
        yield take(UNLOAD_PAGE);
        yield cancel(deamon);
    }
}

export default [watchChangeSelectedDay, watchLoadContactSensorPageSaga];



// WEBPACK FOOTER //
// ./src/core/state/products/contact-sensor/sagas.js